import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Check, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "react-toastify";

import "../../../style/returnsRegister.css";
import ActiveRegistrationIcon from "../../../assets/active-registration-icon.svg";
import DeliveredRecordsIcon from "../../../assets/delivered-records-icon.svg";
import TotalReturnValueIcon from "../../../assets/total-return-value-icon.svg";
import FilterIcon from "../../../assets/icons/FilterIcon";
import SearchIcon from "../../../assets/search-icon.svg";
import NoDataIcon from "../../../assets/icons/NoDataIcon";
import EyeIcon from "../../../assets/icons/EyeIcon.js";
import { returnVendorAPI } from "../../../services";
import { useSelector } from "react-redux";
import MaskedCurrencyValue from "../../common/MaskedCurrencyValue";

/* ---------------- Tooltip Component ---------------- */
const Tooltip = ({ children, content, show }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);

  useEffect(() => {
    if (isVisible && triggerRef.current && tooltipRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let top = triggerRect.bottom + 8;
      let left =
        triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;

      // Adjust if tooltip goes off screen
      if (left < 8) left = 8;
      if (left + tooltipRect.width > viewportWidth - 8) {
        left = viewportWidth - tooltipRect.width - 8;
      }
      if (top + tooltipRect.height > viewportHeight - 8) {
        top = triggerRect.top - tooltipRect.height - 8;
      }

      setPosition({ top, left });
    }
  }, [isVisible]);

  if (!show) return children;

  return (
    <>
      <span
        ref={triggerRef}
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        style={{ cursor: "help" }}
      >
        {children}
      </span>
      {isVisible && (
        <div
          ref={tooltipRef}
          className="returns-tooltip"
          style={{
            position: "fixed",
            top: position.top,
            left: position.left,
            zIndex: 1000,
          }}
        >
          {content}
        </div>
      )}
    </>
  );
};

function VendorReturns() {
  const { isPartsURL } = useSelector((state) => state.domain);
  const navigate = useNavigate();
  const [returns, setReturns] = useState([]);
  const [filteredReturns, setFilteredReturns] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(25);
  // const [sortField, setSortField] = useState("");
  // const [sortDirection, setSortDirection] = useState("asc");
  const [balanceFilter, ] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, ] = useState("this_month");
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [, setShowDateFilterDropdown] = useState(false);
  const [loading, setLoading] = useState(false);

  const [returnStats, setReturnStats] = useState({
    active_records: 0,
    delivered_records: 0,
    total_return_value: 0,
  });
  const [statsLoading, setStatsLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [, setTotalCount] = useState(0);
  const [openStatusDropdown, setOpenStatusDropdown] = useState(null); // Track which return's status dropdown is open
  const [updatingStatus, setUpdatingStatus] = useState(null); // Track which return's status is being updated
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 }); // Position for fixed dropdown
  const filterDropdownRef = useRef(null);
  const dateFilterDropdownRef = useRef(null);
  const statusDropdownRefs = useRef({}); // Refs for status dropdowns
  const statusTriggerRefs = useRef({}); // Refs for status trigger buttons
  const hasLoadedInitialData = useRef(false); // Prevent duplicate initial API calls
  const isFirstRender = useRef(true); // Track first render to prevent search effect on mount

  // Fetch Supplier returns list with filters and pagination
  const fetchReturns = async (
    search = "",
    page = 1,
    type = "",
    status = "all",
    dateFilter = "this_month"
  ) => {
    setLoading(true);
    try {
      // Map date filter value to human-readable label
      // Note: "this_month" is the default, so we send empty string to omit the parameter
      const dateFilterMap = {
        today: "Today",
        yesterday: "Yesterday",
        last_7_days: "Last 7 Days",
        this_month: "", // Default - don't send parameter
        all: "",
      };
      const dateFilterLabel =
        dateFilterMap[dateFilter] !== undefined
          ? dateFilterMap[dateFilter]
          : dateFilter;

      // Map status filter value to human-readable label
      const statusFilterMap = {
        all: "",
        in_process: "In Process",
        successfully_returned: "Successfully Returned",
        delivered: "Delivered",
      };
      const statusFilterLabel = statusFilterMap[status] || status;

      const { response, error } = await returnVendorAPI.getVendorReturnsList(
        search,
        page,
        itemsPerPage,
        statusFilterLabel,
        dateFilterLabel,
        type
      );

      if (error) {
        toast.error(error.message || "Failed to fetch returns");
        setReturns([]);
        return;
      }

      // Handle API response structure
      // API typically returns { results: [], count: number, next: string, previous: string }
      const returnsList = response?.results || response?.data || response || [];
      const totalItems = response?.count || returnsList.length;
      const calculatedPages = Math.ceil(totalItems / itemsPerPage);

      setReturns(returnsList);
      setTotalCount(totalItems);
      setTotalPages(calculatedPages);
    } catch (error) {
      console.error("Error fetching returns:", error);
      toast.error("Failed to fetch returns");
      setReturns([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchReturnStats = async () => {
    setStatsLoading(true);
    try {
      const { response, error } = await returnVendorAPI.getVendorReturnsCount();

      if (error) {
        toast.error(error.message || "Failed to fetch return statistics");
        setReturnStats({
          active_records: 0,
          delivered_records: 0,
          total_return_value: 0,
        });
        return;
      }

      setReturnStats(
        response || {
          active_records: 0,
          delivered_records: 0,
          total_return_value: 0,
        }
      );
    } catch (error) {
      console.error("Error fetching return stats:", error);
      toast.error("Failed to fetch return statistics");
      setReturnStats({
        active_records: 0,
        delivered_records: 0,
        total_return_value: 0,
      });
    } finally {
      setStatsLoading(false);
    }
  };

  // Initial data load - only once, even in StrictMode
  useEffect(() => {
    if (!hasLoadedInitialData.current) {
      hasLoadedInitialData.current = true;
      fetchReturnStats();
      const type =
        balanceFilter === "positive"
          ? "credit"
          : balanceFilter === "negative"
          ? "debit"
          : "";
      fetchReturns(searchTerm, currentPage, type, statusFilter, dateFilter);
    }
    
    // Reset isFirstRender on mount (this runs on every mount, including StrictMode remounts)
    isFirstRender.current = true;
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        filterDropdownRef.current &&
        !filterDropdownRef.current.contains(event.target)
      ) {
        setShowFilterDropdown(false);
      }
      if (
        dateFilterDropdownRef.current &&
        !dateFilterDropdownRef.current.contains(event.target)
      ) {
        setShowDateFilterDropdown(false);
      }
      // Check if click is outside any status dropdown
      const clickedInsideStatusDropdown = Object.values(
        statusDropdownRefs.current
      ).some((ref) => ref && ref.contains(event.target));
      // Check if click is on the trigger button
      const clickedOnTrigger = Object.values(statusTriggerRefs.current).some(
        (ref) => ref && ref.contains(event.target)
      );
      if (
        !clickedInsideStatusDropdown &&
        !clickedOnTrigger &&
        openStatusDropdown !== null
      ) {
        setOpenStatusDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openStatusDropdown]);

  // Update dropdown position on scroll
  useEffect(() => {
    if (openStatusDropdown !== null) {
      const updatePosition = () => {
        const triggerEl = statusTriggerRefs.current[openStatusDropdown];
        if (triggerEl) {
          const rect = triggerEl.getBoundingClientRect();
          setDropdownPosition({
            top: rect.bottom + 4,
            left: rect.left,
          });
        }
      };

      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);

      return () => {
        window.removeEventListener("scroll", updatePosition, true);
        window.removeEventListener("resize", updatePosition);
      };
    }
  }, [openStatusDropdown]);

  // Handle search with API call (skip initial mount to avoid duplicate call)
  useEffect(() => {
    // Skip the first render to prevent duplicate API call on mount
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    const delayedSearch = setTimeout(() => {
      const type =
        balanceFilter === "positive"
          ? "credit"
          : balanceFilter === "negative"
          ? "debit"
          : "";
      fetchReturns(searchTerm, 1, type, statusFilter, dateFilter);
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(delayedSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, balanceFilter, statusFilter, dateFilter]);

  // Set filtered returns to match API results
  // API already handles filtering, so we just set the filtered returns
  useEffect(() => {
    setFilteredReturns(returns);
  }, [returns]);

  // const handleSort = (field) => {
  //   if (sortField === field) {
  //     setSortDirection(sortDirection === "asc" ? "desc" : "asc");
  //   } else {
  //     setSortField(field);
  //     setSortDirection("asc");
  //   }
  // };

  // Use API statistics
  const activeRecords = returnStats.active_records || 0;
  const deliveredRecords = returnStats.delivered_records || 0;
  const totalReturnValue = returnStats.total_return_value || 0;

  // Format numbers for display
  const formatNumber = (num) => {
    return new Intl.NumberFormat("en-US").format(num || 0);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "EUR",
    }).format(amount || 0);
  };

  // Pagination - API handles pagination, so we use the full filtered results
  const currentReturns = filteredReturns;

  // const SortableHeader = ({ field, children }) => (
  //   <th className="sortable-header" onClick={() => handleSort(field)}>
  //     {children}
  //     {sortField === field && (
  //       <span className="ms-1">{sortDirection === "asc" ? "↑" : "↓"}</span>
  //     )}
  //   </th>
  // );

  // Get status badge with appropriate color
  const getStatusBadge = (status) => {
    if (!status) return null;

    const statusLower = status.toLowerCase();
    let className = "returns-status-badge";
    let displayText = status;

    if (statusLower === "under_repair" || statusLower === "under repair") {
      className += " returns-status-under-repair";
      displayText = "Under Repair";
    } else if (
      statusLower === "not_repairable" ||
      statusLower === "not repairable"
    ) {
      className += " returns-status-not-repairable";
      displayText = "Not Repairable";
    } else if (statusLower === "in_process" || statusLower === "in process") {
      className += " returns-status-in-process";
      displayText = "In Process";
    } else if (statusLower === "completed") {
      className += " returns-status-completed";
      displayText = "Completed";
    } else if (
      statusLower === "successfully_returned" ||
      statusLower === "successfully returned"
    ) {
      className += " returns-status-successfully-returned";
      displayText = "Successfully Returned";
    } else if (statusLower === "refund") {
      className += " returns-status-refund";
      displayText = "Refund";
    } else {
      className += " returns-status-default";
    }

    return <span className={className}>{displayText}</span>;
  };

  // Truncate text for display with ellipsis
  const truncateText = (text, maxLength = 20) => {
    if (!text && text !== 0) return "-";
    // Convert to string to handle numbers, objects, etc.
    const textStr = String(text);
    if (
      textStr === "null" ||
      textStr === "undefined" ||
      textStr === "[object Object]"
    )
      return "-";
    if (textStr.length <= maxLength) return textStr;
    return `${textStr.substring(0, maxLength)}...`;
  };

  const statusFilterOptions = [
    { value: "all", label: "All Statuses" },
    { value: "in_process", label: "In Process" },
    { value: "successfully_returned", label: "Successfully Returned" },
    { value: "delivered", label: "Delivered" },
  ];

  const getStatusFilterLabel = () => {
    const option = statusFilterOptions.find(
      (opt) => opt.value === statusFilter
    );
    return option ? option.label : "All Statuses";
  };

  const handleStatusFilterSelect = (value) => {
    setStatusFilter(value);
    setShowFilterDropdown(false);
    const type =
      balanceFilter === "positive"
        ? "credit"
        : balanceFilter === "negative"
        ? "debit"
        : "";
    fetchReturns(searchTerm, 1, type, value, dateFilter);
    setCurrentPage(1);
  };


  // Status options for dropdown (In Process, Delivered, Successfully Returned)
  const statusDropdownOptions = [
    { value: "In Process", label: "In Process" },
    { value: "Delivered", label: "Delivered" },
    { value: "Successfully Returned", label: "Successfully Returned" },
  ];

  // Handle status update
  const handleStatusUpdate = async (returnId, newStatus) => {
    setUpdatingStatus(returnId);
    try {
      const { error } = await returnVendorAPI.updateVendorReturnStatus(
        returnId,
        newStatus
      );
      if (error) {
        toast.error(error.message || "Failed to update status");
        return;
      }

      const successMessage = "Status updated successfully";
      toast.success(successMessage);

      // Close dropdown
      setOpenStatusDropdown(null);

      // Refresh the returns list
      const type =
        balanceFilter === "positive"
          ? "credit"
          : balanceFilter === "negative"
          ? "debit"
          : "";
      fetchReturns(searchTerm, currentPage, type, statusFilter, dateFilter);
      // Refresh stats
      fetchReturnStats();
    } catch (error) {
      console.error("Error updating status:", error);
      // Error already shown in toast above
    } finally {
      setUpdatingStatus(null);
    }
  };

  return (
    <>
      {/* Content Area */}
      <div className="card rounded-3 p-4 border-0 mb-1 returns-container">
        <div className="returns-layout">
          {/* Header */}
          <div className="returns-top">
            <div className="returns-header">
              <div>
                <h2 className="returns-title">Supplier Returns</h2>
                <p className="returns-subtitle">
                  Register and track vendor’s returns
                </p>
              </div>
            </div>

            {/* Summary Cards */}
            <div className="row mb-2">
              <div className="col-md-3">
                <div
                  className="card brands-summary-card"
                  style={{
                    border: "1px solid #DCE3EB",
                    borderRadius: "10px",
                  }}
                >
                  <div className="card-body text-center d-flex align-items-start justify-content-between">
                    <div className="d-flex flex-column align-items-start justify-content-center mb-2 gap-2">
                      <h6
                        className="mb-0 text-muted"
                        style={{
                          fontSize: "14px",
                          color: "#6E7781",
                          fontWeight: "400",
                        }}
                      >
                        Active Records
                      </h6>
                      <h4
                        className="mb-0 fw-semibold"
                        style={{
                          fontSize: "22px",
                          color: "#424A53",
                          fontWeight: "500",
                        }}
                      >
                        {statsLoading ? (
                          <div
                            className="spinner-border spinner-border-sm"
                            role="status"
                          >
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        ) : (
                          formatNumber(activeRecords)
                        )}
                      </h4>
                    </div>

                    <div className="d-flex align-items-center justify-content-center">
                      <img
                        src={ActiveRegistrationIcon}
                        alt="Active Registration"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-md-3">
                <div
                  className="card brands-summary-card"
                  style={{
                    border: "1px solid #DCE3EB",
                    borderRadius: "10px",
                  }}
                >
                  <div className="card-body text-center d-flex align-items-start justify-content-between">
                    <div className="d-flex flex-column align-items-start justify-content-center mb-2 gap-2">
                      <h6
                        className="mb-0 text-muted"
                        style={{
                          fontSize: "14px",
                          color: "#6E7781",
                          fontWeight: "400",
                        }}
                      >
                        Delivered Records
                      </h6>
                      <h4
                        className="mb-0 fw-semibold"
                        style={{
                          fontSize: "22px",
                          color: "#424A53",
                          fontWeight: "500",
                        }}
                      >
                        {statsLoading ? (
                          <div
                            className="spinner-border spinner-border-sm"
                            role="status"
                          >
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        ) : (
                          formatNumber(deliveredRecords)
                        )}
                      </h4>
                    </div>

                    <div className="d-flex align-items-center justify-content-center">
                      <img src={DeliveredRecordsIcon} alt="Completed" />
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-md-3">
                <div
                  className="card brands-summary-card"
                  style={{
                    border: "1px solid #DCE3EB",
                    borderRadius: "10px",
                  }}
                >
                  <div className="card-body text-center d-flex align-items-start justify-content-between">
                    <div className="d-flex flex-column align-items-start justify-content-center mb-2 gap-2">
                      <h6
                        className="mb-0 text-muted"
                        style={{
                          fontSize: "14px",
                          color: "#6E7781",
                          fontWeight: "400",
                        }}
                      >
                        Total Return Value
                      </h6>
                      <h4
                        className="mb-0 fw-semibold"
                        style={{
                          fontSize: "22px",
                          color: "#424A53",
                          fontWeight: "500",
                        }}
                      >
                        {statsLoading ? (
                          <div
                            className="spinner-border spinner-border-sm"
                            role="status"
                          >
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        ) : (
                          <MaskedCurrencyValue
                            value={formatCurrency(totalReturnValue)}
                          />
                        )}
                      </h4>
                    </div>
                    <div className="d-flex align-items-center justify-content-center">
                      <img src={TotalReturnValueIcon} alt="Under Repair" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Search + Date Filter + Status Filter */}
            <div className="search-filter-section mb-4">
              {/* Search Bar */}
              <div className="returns-search-container">
                <input
                  type="text"
                  className={`${isPartsURL ? "returns-search-input-parts" : "returns-search-input"}`}
                  placeholder="Search by Return ID, IMEI, customer name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <img
                  src={SearchIcon}
                  alt="Search"
                  className="returns-search-icon"
                />
              </div>

              {/* Statuses Filter */}
              <div className="returns-filter-dropdown" ref={filterDropdownRef}>
                <button
                  className={`${isPartsURL ? "returns-statuses-filter-btn-parts" : "returns-statuses-filter-btn"}`}
                  onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                >
                  <FilterIcon size={16} />
                  {getStatusFilterLabel()}
                  {showFilterDropdown ? (
                    <ChevronUp
                      size={16}
                      className="returns-filter-dropdown-arrow"
                      style={{
                        width: "16px",
                        height: "16px",
                        color: "#424A53",
                      }}
                    />
                  ) : (
                    <ChevronDown
                      size={16}
                      className="returns-filter-dropdown-arrow"
                      style={{
                        width: "16px",
                        height: "16px",
                        color: "#424A53",
                      }}
                    />
                  )}
                </button>

                <div
                  className={`returns-filter-dropdown-content ${
                    showFilterDropdown ? "show" : ""
                  }`}
                >
                  {statusFilterOptions.map((option) => (
                    <div
                      key={option.value}
                      className={`returns-filter-dropdown-item ${
                        statusFilter === option.value ? "selected" : ""
                      }`}
                      onClick={() => handleStatusFilterSelect(option.value)}
                    >
                      <Check
                        size={16}
                        className="returns-filter-dropdown-check"
                      />
                      {option.label}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Returns Table */}
          <div className="returns-table-wrapper">
            <div className="returns-table-responsive">
              <table className="returns-table">
                <thead>
                  <tr>
                    <th>DELIVERY ID</th>
                    <th>SUPPLIER</th>
                    <th>BUYING PRICE</th>
                    <th>TOTAL QTY.</th>
                    <th>TOTAL AMOUNT</th>
                    <th>STATUS</th>
                    <th>ACTION</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="7" className="no-products-message">
                        <div
                          className=""
                          style={{
                            marginBottom: "4rem",
                            marginTop: "4rem",
                          }}
                        >
                          <div className="spinner-border" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          <h3 style={{ marginTop: "2rem" }}>
                            Loading returns...
                          </h3>
                        </div>
                      </td>
                    </tr>
                  ) : currentReturns.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="no-products-message">
                        <div
                          className=""
                          style={{
                            marginBottom: "4rem",
                            marginTop: "4rem",
                          }}
                        >
                          <NoDataIcon />
                          <h3 style={{ marginTop: "2rem" }}>
                            No returns found
                          </h3>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    currentReturns.map((returnItem) => {
                      const returnId =
                        returnItem.return_id ||
                        returnItem.delivery_id ||
                        returnItem.id ||
                        "-";

                      // Handle Supplier - API returns vendor_name directly, vendor is just an ID
                      const vendor = returnItem.vendor_name || "-";

                      const buyingPrice =
                        returnItem.buying_price ||
                        returnItem.buying_price_amount ||
                        0;
                      const totalQty =
                        returnItem.total_qty ||
                        returnItem.total_quantity ||
                        returnItem.quantity ||
                        0;
                      // API returns total_value as string like "11.00"
                      const totalAmount =
                        returnItem.total_value ||
                        returnItem.total_amount ||
                        returnItem.total_amount_value ||
                        0;
                      const status = returnItem.status || "-";

                      const vendorTruncated = truncateText(vendor, 20);
                      const vendorStr = String(vendor || "");
                      const showVendorTooltip =
                        vendor !== "-" &&
                        vendorStr !== "-" &&
                        vendorStr !== "null" &&
                        vendorStr !== "undefined" &&
                        vendorStr.length > 20;

                      return (
                        <tr
                          key={
                            returnItem.id ||
                            returnItem.return_id ||
                            returnItem.delivery_id
                          }
                        >
                          <td>{returnId}</td>
                          <td>
                            <Tooltip content={vendor} show={showVendorTooltip}>
                              <span className="returns-table-cell-truncate">
                                {vendorTruncated}
                              </span>
                            </Tooltip>
                          </td>
                          <td>{formatCurrency(buyingPrice)}</td>
                          <td>{formatNumber(totalQty)}</td>
                          <td>{formatCurrency(totalAmount)}</td>

                          {/* Status Dropdown */}
                          <td>
                            {(() => {
                              const statusLower = (status || "")
                                .toLowerCase()
                                .replace(/_/g, " ");
                              const statusNormalized = statusLower.trim();
                              const returnId =
                                returnItem.id ||
                                returnItem.return_id ||
                                returnItem.delivery_id;
                              const isDropdownOpen =
                                openStatusDropdown === returnId;
                              const isUpdating = updatingStatus === returnId;

                              // Get status badge config
                              let badgeConfig = null;
                              let showDropdown = false;

                              if (statusNormalized === "in process") {
                                badgeConfig = {
                                  className: "in-process-status-badge",
                                  color: "#57606A",
                                  label: "In Process",
                                };
                                showDropdown = true;
                              } else if (statusNormalized === "delivered") {
                                badgeConfig = {
                                  className: "completed-status-badge",
                                  color: "#2F80ED",
                                  label: "Delivered",
                                };
                                showDropdown = true;
                              } else if (
                                statusNormalized === "successfully returned"
                              ) {
                                badgeConfig = {
                                  className:
                                    "successfully-returned-status-badge",
                                  color: "#0A8900",
                                  label: "Successfully Returned",
                                };
                                showDropdown = false; // No dropdown for Successfully Returned
                              } else {
                                return getStatusBadge(status);
                              }

                              if (!badgeConfig) return null;

                              return (
                                <div style={{ position: "relative" }}>
                                  <div
                                    ref={(el) => {
                                      if (el) {
                                        statusTriggerRefs.current[returnId] =
                                          el;
                                      }
                                    }}
                                    className={`d-flex align-items-center gap-2 ${badgeConfig.className}`}
                                    style={{
                                      cursor:
                                        showDropdown && !isUpdating
                                          ? "pointer"
                                          : "default",
                                      opacity: isUpdating ? 0.6 : 1,
                                    }}
                                    onClick={(e) => {
                                      if (showDropdown && !isUpdating) {
                                        e.stopPropagation();
                                        if (isDropdownOpen) {
                                          setOpenStatusDropdown(null);
                                        } else {
                                          // Calculate position for fixed dropdown
                                          const triggerEl =
                                            statusTriggerRefs.current[returnId];
                                          if (triggerEl) {
                                            const rect =
                                              triggerEl.getBoundingClientRect();
                                            setDropdownPosition({
                                              top: rect.bottom + 4,
                                              left: rect.left,
                                            });
                                          }
                                          setOpenStatusDropdown(returnId);
                                        }
                                      }
                                    }}
                                  >
                                    <div
                                      style={{
                                        width: "8px",
                                        height: "8px",
                                        backgroundColor: badgeConfig.color,
                                        borderRadius: "100%",
                                      }}
                                    />
                                    <span>{badgeConfig.label}</span>
                                    {showDropdown && (
                                      <ChevronDown
                                        size={16}
                                        className=""
                                        style={{
                                          width: "16px",
                                          height: "16px",
                                          color: badgeConfig.color,
                                          transform: isDropdownOpen
                                            ? "rotate(180deg)"
                                            : "rotate(0deg)",
                                          transition: "transform 0.2s",
                                        }}
                                      />
                                    )}
                                  </div>

                                  {/* Status Dropdown */}
                                  {isDropdownOpen && showDropdown && (
                                    <div
                                      className="returns-status-dropdown-content"
                                      ref={(el) => {
                                        if (el) {
                                          statusDropdownRefs.current[returnId] =
                                            el;
                                        }
                                      }}
                                      style={{
                                        position: "fixed",
                                        top: `${dropdownPosition.top}px`,
                                        left: `${dropdownPosition.left}px`,
                                        zIndex: 9999,
                                        backgroundColor: "#fff",
                                        border: "1px solid #DCE3EB",
                                        borderRadius: "8px",
                                        boxShadow:
                                          "0 4px 6px rgba(0, 0, 0, 0.1)",
                                        minWidth: "180px",
                                      }}
                                    >
                                      {statusDropdownOptions.map((option) => (
                                        <div
                                          key={option.value}
                                          className={`returns-status-dropdown-item ${
                                            badgeConfig.label === option.label
                                              ? "selected"
                                              : ""
                                          }`}
                                          style={{
                                            padding: "10px 16px",
                                            cursor: "pointer",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "8px",
                                            backgroundColor:
                                              badgeConfig.label === option.label
                                                ? "#F5F7FA"
                                                : "transparent",
                                          }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (
                                              badgeConfig.label !== option.label
                                            ) {
                                              handleStatusUpdate(
                                                returnId,
                                                option.value
                                              );
                                            } else {
                                              setOpenStatusDropdown(null);
                                            }
                                          }}
                                        >
                                          <span
                                            style={{
                                              color:
                                                badgeConfig.label ===
                                                option.label
                                                  ? "#8C959F"
                                                  : "inherit",
                                            }}
                                          >
                                            {option.label}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                          </td>

                          <td>
                            <div className="action-buttons">
                              <button
                                className={`${isPartsURL ? "action-btn-parts view" : "action-btn view"}`}
                                onClick={() =>
                                  navigate(
                                    `/returns/vendor/${
                                      returnItem.id ||
                                      returnItem.return_id ||
                                      returnItem.delivery_id
                                    }`
                                  )
                                }
                                title="Detail"
                              >
                                <EyeIcon fill={isPartsURL ? "var(--primary-color-parts)" : "var(--primary-color-mobile)"} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="returns-pagination-sticky">
              <div className="returns-pagination-container">
                <div className="returns-pagination-info">
                  Page {currentPage} of {totalPages}
                </div>
                <div className="returns-pagination-buttons">
                  <button
                    className="previous"
                    onClick={() => {
                      const newPage = currentPage - 1;
                      setCurrentPage(newPage);
                      const type =
                        balanceFilter === "positive"
                          ? "credit"
                          : balanceFilter === "negative"
                          ? "debit"
                          : "";
                      fetchReturns(
                        searchTerm,
                        newPage,
                        type,
                        statusFilter,
                        dateFilter
                      );
                    }}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>
                  <button
                    className={`${isPartsURL ? "next-returns-pagination-btn-parts" : "next-returns-pagination-btn"}`}
                    onClick={() => {
                      const newPage = currentPage + 1;
                      setCurrentPage(newPage);
                      const type =
                        balanceFilter === "positive"
                          ? "credit"
                          : balanceFilter === "negative"
                          ? "debit"
                          : "";
                      fetchReturns(
                        searchTerm,
                        newPage,
                        type,
                        statusFilter,
                        dateFilter
                      );
                    }}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default VendorReturns;
